import { Fragment, useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, PenLine, Shield } from 'lucide-react';
import { lettersApi } from '../../services/api';
import { SignaturePreview } from './SignaturePreview';

interface LettersAdminPageProps {
  onNavigate: (page: string) => void;
}

interface SignatureManagementUser {
  id: number;
  name: string;
  email: string;
  has_signature: boolean | number;
  is_admin: boolean | number;
  is_authorized_signatory: boolean | number;
}

export function LettersAdminPage({ onNavigate }: LettersAdminPageProps) {
  const [rows, setRows] = useState<SignatureManagementUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [previewByUserId, setPreviewByUserId] = useState<Record<number, string>>({});
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);
  const [updatingUserTypeId, setUpdatingUserTypeId] = useState<number | null>(null);

  const loadRows = useCallback(async ({ silent, signal }: { silent: boolean; signal?: AbortSignal }) => {
    try {
      if (!silent) {
        setLoading(true);
        setError('');
      }
      const response = await lettersApi.listSignatureManagementUsers({ signal });
      setRows(response.data || []);
    } catch (err: any) {
      if (signal?.aborted) return;
      if (silent) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to load signature management users.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  // NOTE: no background polling here. The list is loaded once on mount and on
  // manual Refresh only — auto-refresh every 30s was re-rendering the whole table
  // under the user and made the page feel like it was constantly refreshing.
  useEffect(() => {
    loadRows({ silent: false });
  }, []);

  const handleUserTypeChange = async (row: SignatureManagementUser, nextType: 'user' | 'admin') => {
    const nextIsAdmin = nextType === 'admin';
    const currentIsAdmin = row.is_admin === true || row.is_admin === 1;
    if (nextIsAdmin === currentIsAdmin) return;

    try {
      setUpdatingUserTypeId(row.id);
      setError('');
      await lettersApi.updateRoleAssignment(row.id, {
        is_admin: nextIsAdmin,
        is_authorized_signatory: row.is_authorized_signatory === true || row.is_authorized_signatory === 1,
      });

      setRows((prev) => prev.map((item) => (
        item.id === row.id
          ? { ...item, is_admin: nextIsAdmin ? 1 : 0 }
          : item
      )));
      loadRows({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update user type.');
    } finally {
      setUpdatingUserTypeId(null);
    }
  };

  const openSignatureDesignerForUser = (userId: number) => {
    localStorage.setItem('letters_signature_target_user_id', String(userId));
    localStorage.setItem('letters_signature_open_designer', '1');
    localStorage.setItem('letters_signature_from_admin', '1');
    onNavigate('letters-add-signature');
  };

  const toggleExpand = async (row: SignatureManagementUser) => {
    const hasSignature = row.has_signature === true || row.has_signature === 1;

    if (expandedRowId === row.id) {
      setExpandedRowId(null);
      return;
    }

    setExpandedRowId(row.id);

    if (!hasSignature || previewByUserId[row.id] !== undefined) {
      return;
    }

    try {
      setPreviewLoadingId(row.id);
      const response = await lettersApi.getSignatorySignature(row.id);
      const signatureHtml = response?.data?.signature_html || '';
      setPreviewByUserId((prev) => ({ ...prev, [row.id]: signatureHtml }));
    } catch {
      setPreviewByUserId((prev) => ({ ...prev, [row.id]: '' }));
    } finally {
      setPreviewLoadingId(null);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Letter Admin</h1>
        <p className="text-gray-600 mt-2">Manage signature setup for users who have access to the Letter Generation module.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 md:p-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Signature Management</h2>
            <p className="text-sm text-gray-600 mt-1">Only users with Letter Generation module access are listed below.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadRows({ silent: false })}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Refresh
            </button>
            <button
              onClick={() => onNavigate('letters-add-signature')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 text-sm"
            >
              <PenLine className="w-4 h-4" /> Manage Signatures
            </button>
          </div>
        </div>

        {error ? <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">⚠ {error}</div> : null}

        <div className="overflow-auto border border-gray-200 rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3 font-semibold w-14">View</th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email ID</th>
                <th className="text-left px-4 py-3 font-semibold">Signature Status</th>
                <th className="text-left px-4 py-3 font-semibold">User Type</th>
                <th className="text-left px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-gray-500">
                    <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading users...</span>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-gray-500">No users with Letter Generation module access found.</td>
                </tr>
              ) : (
                rows.map((row) => {
                  const hasSignature = row.has_signature === true || row.has_signature === 1;
                  const isAdminUser = row.is_admin === true || row.is_admin === 1;
                  const isExpanded = expandedRowId === row.id;
                  const previewHtml = previewByUserId[row.id] || '';
                  return (
                    <Fragment key={row.id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleExpand(row)}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{row.name}</td>
                        <td className="px-4 py-3 text-gray-600">{row.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${hasSignature ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {hasSignature ? 'Created' : 'Not Created'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={isAdminUser ? 'admin' : 'user'}
                            disabled={updatingUserTypeId === row.id}
                            onChange={(e) => handleUserTypeChange(row, e.target.value as 'user' | 'admin')}
                            className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-60"
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => openSignatureDesignerForUser(row.id)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 text-xs font-medium"
                          >
                            {hasSignature ? 'Edit Signature' : 'Create Signature'}
                          </button>
                        </td>
                      </tr>

                      {isExpanded ? (
                        <tr className="border-b border-gray-100 bg-gray-50/60">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="rounded-xl border border-gray-200 bg-white p-4">
                              <div className="text-sm font-medium text-gray-800 mb-3">Signature Preview</div>

                              {hasSignature ? (
                                previewLoadingId === row.id ? (
                                  <div className="text-sm text-gray-500 inline-flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading preview...
                                  </div>
                                ) : previewHtml ? (
                                  <SignaturePreview html={previewHtml} />
                                ) : (
                                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                                    Signature preview unavailable.
                                  </div>
                                )
                              ) : (
                                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                                  Empty. No signature created for this user.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 inline-flex items-center gap-2">
        <Shield className="w-4 h-4" /> Only letter admins can access this section.
      </div>
    </div>
  );
}
